<template>
  <div class="login-container">
    <!-- Gradient Background -->
    <div class="gradient-bg"></div>

    <!-- Login Card -->
    <div class="login-card">
      <!-- Header -->
      <div class="login-header">
        <div class="logo-circle">🍜</div>
        <h1>Xôi A Diểng</h1>
        <p class="subtitle">Hệ thống quản lý đơn hàng</p>
      </div>

      <!-- Form -->
      <form @submit.prevent="submit" class="login-form">
        <!-- Username Input -->
        <div class="form-group">
          <label for="username">👤 Tài khoản</label>
          <input
            id="username"
            v-model="username"
            type="text"
            placeholder="Nhập tài khoản của bạn"
            :disabled="isLoading"
            class="form-input"
            @keyup.enter="submit"
          />
        </div>

        <!-- Password Input -->
        <div class="form-group">
          <label for="password">🔒 Mật khẩu</label>
          <input
            id="password"
            v-model="password"
            type="password"
            placeholder="Nhập mật khẩu của bạn"
            :disabled="isLoading"
            class="form-input"
            @keyup.enter="submit"
          />
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          ⚠️ {{ errorMessage }}
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="isLoading || !username || !password"
          class="btn-login"
        >
          <span v-if="!isLoading">🚀 Đăng nhập</span>
          <span v-else class="loading-spinner">⏳ Đang xử lý...</span>
        </button>
      </form>

      <!-- Footer -->
      <div class="login-footer">
        <p>© 2024 Xôi A Diểng - Xôi nóng, topping đầy đặn</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { login } from '../services/auth'

const username = ref('')
const password = ref('')
const isLoading = ref(false)
const errorMessage = ref('')

async function submit() {
  // Reset error message
  errorMessage.value = ''

  // Validate inputs
  if (!username.value.trim() || !password.value.trim()) {
    errorMessage.value = 'Vui lòng nhập tài khoản và mật khẩu'
    return
  }

  isLoading.value = true

  try {
    await login(username.value, password.value)
    // Success - redirect will be handled by auth service
  } catch (error) {
    console.error('Login error:', error)
    errorMessage.value = error.response?.data?.message || 'Tài khoản hoặc mật khẩu không chính xác'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.gradient-bg {
  position: absolute;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 107, 53, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  top: -100px;
  left: -100px;
  pointer-events: none;
}

.login-card {
  position: relative;
  z-index: 1;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 400px;
  padding: 40px;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.logo-circle {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto 15px;
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.3);
}

.login-header h1 {
  color: #2c3e50;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 5px;
}

.subtitle {
  color: #7f8c8d;
  font-size: 13px;
  letter-spacing: 0.5px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #2c3e50;
  font-weight: 600;
  font-size: 14px;
}

.form-input {
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
}

.form-input:focus {
  outline: none;
  border-color: #ff6b35;
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
}

.form-input:disabled {
  background-color: #f0f0f0;
  color: #999;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #bdc3c7;
}

.error-message {
  background-color: #ffe6e6;
  color: #d32f2f;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  border-left: 4px solid #d32f2f;
  animation: shake 0.3s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.btn-login {
  padding: 12px 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
  letter-spacing: 0.5px;
  margin-top: 10px;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(255, 107, 53, 0.4);
}

.btn-login:active:not(:disabled) {
  transform: translateY(0);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.login-footer {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  color: #7f8c8d;
  font-size: 12px;
}

/* Responsive Design */
@media (max-width: 480px) {
  .login-card {
    width: 90%;
    padding: 30px 20px;
  }

  .login-header h1 {
    font-size: 24px;
  }

  .logo-circle {
    width: 70px;
    height: 70px;
    font-size: 35px;
  }

  .form-input {
    padding: 10px 12px;
    font-size: 16px; /* Prevents zoom on iOS */
  }

  .btn-login {
    padding: 11px 16px;
    font-size: 14px;
  }
}
</style>
